import { useState, useEffect, useRef } from 'react'

const POLL_MS = 3 * 60 * 1000

async function fetchBuild(): Promise<string | null> {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' })
    if (!res.ok) return null
    const data: unknown = await res.json()
    return data !== null && typeof data === 'object' && 'build' in data
      ? String((data as { build: unknown }).build)
      : null
  } catch {
    return null
  }
}

export function useVersionCheck(): { updateAvailable: boolean } {
  const initialBuild = useRef<string | null>(null)
  const [updateAvailable, setUpdateAvailable] = useState(false)

  useEffect(() => {
    let mounted = true

    async function check() {
      const build = await fetchBuild()
      if (!mounted || build === null || build === 'dev') return

      if (initialBuild.current === null) {
        initialBuild.current = build
        return
      }

      if (build !== initialBuild.current) {
        setUpdateAvailable(true)
      }
    }

    void check()

    const timer = setInterval(() => void check(), POLL_MS)

    function onVisible() {
      if (document.visibilityState === 'visible') void check()
    }

    function onFocus() {
      void check()
    }

    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onFocus)

    return () => {
      mounted = false
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onFocus)
    }
  }, [])

  return { updateAvailable }
}
